import { useCallback, useEffect, useRef, useState } from "react";
import { useTouchDrag } from "@/hooks/useTouchDrag";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Settings, History } from "lucide-react";
import { toast } from "sonner";
import { api, initials, reorder } from "@/lib/api";
import { Header } from "@/components/Header";
import { SimpleModal } from "@/components/Modal";
import { BoardToolbar, matches } from "@/components/board/BoardToolbar";
import { BoardColumn } from "@/components/board/BoardColumn";
import { TaskModal } from "@/components/board/TaskModal";
import { ColumnModal } from "@/components/board/ColumnModal";
import { Comments } from "@/components/board/Comments";
import { MembersModal } from "@/components/board/MembersModal";
import { BoardSettingsModal } from "@/components/board/BoardSettingsModal";
import { ActivityPanel } from "@/components/board/ActivityPanel";

export default function BoardPage({ user, onLogout }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ query: "", priority: "all", assignee: "all" });
  const [modal, setModal] = useState(null);
  const [drag, setDrag] = useState(null);
  const [colDrag, setColDrag] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [version, setVersion] = useState(0);
  const load = useCallback(() => api(`/boards/${id}`).then((b) => { setBoard(b); setVersion((v) => v + 1); }).catch((e) => setError(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  const columnsRef = useRef(null);
  const touchDrop = useRef(() => {});
  const touchStart = useTouchDrag({
    onBegin: (item) => (item.kind === "task" ? setDrag(item.data) : setColDrag(item.data)),
    onEnd: () => { setDrag(null); setColDrag(null); },
    onDrop: (...args) => touchDrop.current(...args),
    scrollEl: columnsRef,
  });

  const attempt = async (fn) => { try { await fn(); } catch (e) { toast.error(e.message); } finally { load(); } };
  const close = () => setModal(null);

  if (error) return (
    <div className="app-shell"><Header user={user} onLogout={onLogout} />
      <main className="dashboard" data-testid="board-error"><p className="eyebrow">CAN'T OPEN BOARD</p><h1 className="error-title">{error}</h1><p className="muted">Ask the board owner to invite you, or head back to your boards.</p><button className="primary" data-testid="board-error-back" onClick={() => nav("/")}>← Back to boards</button></main>
    </div>
  );
  if (!board) return <div className="loading">Loading board…</div>;

  const dropTask = (task, columnId, index) => {
    setDrag(null);
    setBoard((b) => ({ ...b, columns: reorder(b.columns, task, columnId, index) }));
    attempt(() => api(`/tasks/${task.id}/move`, { method: "PATCH", body: JSON.stringify({ column_id: columnId, position: index }) }));
  };
  const moveColumn = (column, index) => {
    setBoard((b) => {
      const others = b.columns.filter((c) => c.id !== column.id);
      others.splice(index, 0, b.columns.find((c) => c.id === column.id));
      return { ...b, columns: others };
    });
    attempt(() => api(`/columns/${column.id}/move`, { method: "PATCH", body: JSON.stringify({ position: index }) }));
  };
  const dropColumn = (column, targetId, after) => {
    setColDrag(null);
    if (column.id === targetId) return;
    moveColumn(column, board.columns.filter((c) => c.id !== column.id).findIndex((c) => c.id === targetId) + (after ? 1 : 0));
  };
  const onDropAt = (columnId, index) => drag && dropTask(drag, columnId, index);
  const onColumnDrop = (target, after) => colDrag && dropColumn(colDrag, target.id, after);
  touchDrop.current = (item, x, y) => {
    const el = document.elementFromPoint(x, y);
    const colEl = el?.closest("[data-column-id]");
    if (!colEl) return;
    const colId = Number(colEl.dataset.columnId);
    const half = (node, horizontal) => { const r = node.getBoundingClientRect(); return horizontal ? x > r.left + r.width / 2 : y > r.top + r.height / 2; };
    if (item.kind === "column") return dropColumn(item.data, colId, half(colEl, true));
    const siblings = board.columns.find((c) => c.id === colId).tasks.filter((t) => t.id !== item.data.id);
    const card = el.closest("[data-task-id]");
    if (!card) return dropTask(item.data, colId, siblings.length);
    const targetId = Number(card.dataset.taskId);
    if (targetId !== item.data.id) dropTask(item.data, colId, siblings.findIndex((t) => t.id === targetId) + (half(card) ? 1 : 0));
  };
  const saveTask = (d) => attempt(async () => {
    if (modal.task) await api(`/tasks/${modal.task.id}`, { method: "PATCH", body: JSON.stringify(d) });
    else await api(`/boards/${id}/tasks`, { method: "POST", body: JSON.stringify(d) });
    close();
  });
  const deleteTask = (t) => window.confirm(`Delete "${t.title}"?`) && attempt(() => api(`/tasks/${t.id}`, { method: "DELETE" }));
  const addColumn = (name) => attempt(async () => { await api(`/boards/${id}/columns`, { method: "POST", body: JSON.stringify({ name }) }); close(); });

  return (
    <div className="app-shell board-page">
      <Header user={user} onLogout={onLogout} />
      <main className="board-main">
        <div className="board-heading">
          <div>
            <button className="back-button" data-testid="back-to-dashboard" onClick={() => nav("/")}>← All boards</button>
            <h1 data-testid="board-title">{board.name}</h1><p>{board.description}</p>
          </div>
          <div className="member-stack">
            {board.members.map((m) => <span key={m.id} title={m.name} data-testid={`member-avatar-${m.id}`}>{initials(m.name)}</span>)}
            <button className="add-member" data-testid="add-member-button" aria-label="Manage members" onClick={() => setModal({ type: "member" })}><Plus size={15} /></button>
            <button className="ghost activity-button" data-testid="activity-button" onClick={() => setShowActivity(true)}><History size={16} /> Activity</button>
            {board.owner_id === user.id && <button className="icon-button board-settings" data-testid="board-settings-button" aria-label="Board settings" onClick={() => setModal({ type: "board" })}><Settings size={17} /></button>}
          </div>
        </div>
        <BoardToolbar filters={filters} setFilters={setFilters} members={board.members} />
        <div className="columns" data-testid="kanban-board" ref={columnsRef}>
          {board.columns.map((col) => <BoardColumn key={col.id} col={col} visible={matches(filters)} drag={drag} setDrag={setDrag} colDrag={colDrag} setColDrag={setColDrag} onDropAt={onDropAt} onColumnDrop={onColumnDrop} touchStart={touchStart} setModal={setModal} deleteTask={deleteTask} />)}
          <button className="add-column" data-testid="add-column-button" onClick={() => setModal({ type: "add-column" })}><Plus size={18} /> Add column</button>
        </div>
      </main>
      {modal?.type === "task" && <TaskModal task={modal.task} column={modal.column || board.columns[0]} board={board} close={close} save={saveTask} />}
      {modal?.type === "comments" && <Comments task={modal.task} close={close} user={user} onChange={load} />}
      {modal?.type === "add-column" && <SimpleModal title="Add column" placeholder="Column name" close={close} onSave={addColumn} />}
      {modal?.type === "column" && <ColumnModal column={modal.column} index={board.columns.findIndex((c) => c.id === modal.column.id)} total={board.columns.length} onMove={(i) => moveColumn(modal.column, i)} close={close} reload={load} />}
      {modal?.type === "member" && <MembersModal board={board} user={user} close={close} setBoard={setBoard} />}
      {modal?.type === "board" && <BoardSettingsModal board={board} close={close} setBoard={setBoard} />}
      {showActivity && <ActivityPanel boardId={id} version={version} close={() => setShowActivity(false)} />}
    </div>
  );
}
